import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { CheckCircle2, Circle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/authContext';
import { api, throwIfNotOk } from '../lib/api';
import { clearClientIdentity } from '../lib/auth';
import { qk } from '../lib/queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@siliconx/shared/ui/utils';
import { LoadingShell, ErrorState } from '@siliconx/shared/ui/state';
import type { OrgAddress, IndividualProfile, IndividualPreferences, CountryCode } from '@siliconx/shared';
import {
  guessDefaultDialCountry,
  reformatPhoneWithCaret,
  isValidPhoneForCountry,
  toE164,
  formatPhoneForDisplay,
  phoneValidationMessage,
} from '@siliconx/shared';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { TextInput } from '@siliconx/shared/ui/Field';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import { FieldStatusMarker, SavedIndicator } from '../components/FieldStatusMarker';
import { PhoneField } from '../components/PhoneField';

/**
 * SIL-993 — individual customer_org personal profile completion.
 *
 * The single authoritative record for an individual (861/individual_profile) —
 * NOT the business dual-profile (seller_org/buyer_org) editor at /profile
 * (SIL-994 wires portal navigation/labels; this route is reachable directly
 * and via the first-sign-in redirect in __root.tsx today).
 *
 * Completing the required personal fields (first name, last name, email,
 * phone, address) unlocks BOTH buying and selling in one step — there is no
 * separate buyer/seller completion state for an individual (approved design).
 * Buying/selling PREFERENCES are optional — they are not part of the unlock
 * gate (individualProfileCompleteness, packages/shared/src/dto/individual-profile.ts).
 *
 * SIL-1196 (QA UX-04 second pass, follows SIL-1190):
 *  - Every field carries an explicit Required/Optional marker in its label
 *    row (no bare `*` convention) — icon + text, never color alone.
 *  - The 6-item server-shaped checklist is the ONE counter now (the old
 *    10-field live counter is gone per JR's 2026-09-22 decision); its rows
 *    are links that scroll to AND focus their field.
 *  - Every editable field autosaves on blur (preference pills autosave on
 *    toggle, since they have no natural blur), with a per-section "Saved"
 *    indicator. The bottom button is "save and unlock" framing, not a bare
 *    "Save" — it reports what's still missing rather than pretending a
 *    partial save finished the job.
 */
/**
 * SIL-1210: `?return=<path>` is an origin marker set by the Confirm-screen
 * CTAs (ProbeSessionSummary, AddAssetSuccessPanel) — "the user who opened
 * this page from there should land back there, with a confirmation, once
 * their profile becomes complete." A user who opens this page from
 * Settings (no marker) is never redirected — see the completion effect
 * below, which additionally requires the marker AND an incomplete→complete
 * transition, not just presence of the marker.
 *
 * Same same-origin-only shape as `stashReturnTo`
 * (packages/shared/src/auth/client.ts) — a value that fails the check is
 * silently dropped (undefined), not surfaced as an error, so a malicious
 * `?return=` never reaches `navigate()`.
 */
export function isSafeReturnPath(value: string): boolean {
  if (!value.startsWith('/')) return false;
  if (value.startsWith('//')) return false; // protocol-relative
  if (value.startsWith('/\\')) return false; // browsers treat backslash as a path separator too
  if (value.includes('://')) return false; // absolute URL smuggled in after the leading slash
  return true;
}

export const Route = createFileRoute('/individual-profile')({
  validateSearch: (search: Record<string, unknown>): { return?: string } => {
    const raw = search.return;
    return typeof raw === 'string' && isSafeReturnPath(raw) ? { return: raw } : {};
  },
  component: IndividualProfilePage,
});

/**
 * RUF-540-style completeness checklist, mirroring profile.tsx's
 * CompletenessChecklist — keys match individualProfileCompleteness()'s
 * missing-field keys verbatim.
 *
 * SIL-1190: 'address' covers all five address fields now (street, city,
 * state, postal code, country) — the label below is the one place this
 * checklist and the per-field indicators on the form itself must agree.
 *
 * SIL-1196: this is now the ONLY completion counter on the page (the old
 * "N of 10" live counter is gone) — see COMPUTED doneMap in the page
 * component, derived live from the form's own state, not from the
 * (save-triggered) server missingFields list.
 */
const COMPLETENESS_FIELDS: { key: string; label: string }[] = [
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'address', label: 'Address (street, city, state, postal code, country)' },
  { key: 'countryOfResidence', label: 'Country of residence' },
];

/**
 * SIL-1196: the granular required-input list backing the "N fields still
 * needed" summary and its jump links on an incomplete Save — finer-grained
 * than COMPLETENESS_FIELDS (which groups all 5 address fields as one
 * "address" item), since a jump link has to land on one specific input.
 * `email` is excluded — it's a read-only system field the user can't act on.
 */
const REQUIRED_INPUT_FIELDS: { key: string; id: string; label: string }[] = [
  { key: 'firstName', id: 'field-firstName', label: 'First name' },
  { key: 'lastName', id: 'field-lastName', label: 'Last name' },
  { key: 'phone', id: 'field-phone', label: 'Phone' },
  { key: 'addrStreet', id: 'field-addrStreet', label: 'Street' },
  { key: 'addrCity', id: 'field-addrCity', label: 'City' },
  { key: 'addrState', id: 'field-addrState', label: 'State' },
  { key: 'addrPostal', id: 'field-addrPostal', label: 'Postal code' },
  { key: 'addrCountry', id: 'field-addrCountry', label: 'Country' },
  { key: 'countryOfResidence', id: 'field-countryOfResidence', label: 'Country of residence' },
];

const ADDRESS_FIELD_IDS = ['field-addrStreet', 'field-addrCity', 'field-addrState', 'field-addrPostal', 'field-addrCountry'];

/**
 * SIL-1190: ISO 3166-1 alpha-2 code + display name, derived from the
 * browser's own region data — mirrors onboarding.tsx's COUNTRY_OPTIONS (the
 * "organization country" select). Used for countryOfResidence so an invalid
 * free-text value (the "USA" bug) can no longer be typed in the first
 * place; address.country stays free text (it is not format-validated and
 * is unrelated to this compliance field, per SIL-1011).
 */
const COUNTRY_OPTIONS: { code: string; name: string }[] = (() => {
  if (typeof Intl.DisplayNames !== 'function') return [];
  const names = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'code' });
  const out: { code: string; name: string }[] = [];
  for (let a = 65; a <= 90; a++) {
    for (let b = 65; b <= 90; b++) {
      const code = String.fromCharCode(a, b);
      const name = names.of(code);
      if (name && name !== code) out.push({ code, name });
    }
  }
  return out.sort((x, y) => x.name.localeCompare(y.name));
})();

/**
 * SIL-1198 — best-effort ISO-2 region guess from the browser's own locale
 * list, used only as the LAST fallback in `guessDefaultDialCountry` (the
 * address country and an already-set phone value both take priority). Never
 * throws — `navigator`/`navigator.languages` can be absent in a test/SSR
 * environment.
 */
function browserLocaleCountryGuess(): string | null {
  if (typeof navigator === 'undefined') return null;
  const langs = navigator.languages && navigator.languages.length > 0 ? navigator.languages : [navigator.language];
  for (const lang of langs) {
    if (!lang) continue;
    const region = lang.split('-').pop();
    if (region && region.length === 2) return region.toUpperCase();
  }
  return null;
}

/** Scrolls a field into view and moves real focus to it (checklist links, jump links, incomplete-save focus). */
function focusAndScroll(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  el.focus?.({ preventScroll: true });
}

/**
 * SIL-1190/1196: per-field required/complete/invalid indicator, plus
 * autosave-on-blur. `complete` is computed live from the field's own current
 * value (not the server's post-save missingFields list), so it updates on
 * every keystroke. Neutral (green/gray) icon colors, not brand — the one
 * accent focal point on this page is the Save button (siliconx-design-
 * system: SIL-1184's "one accent per view" convention).
 */
function TextField({
  id,
  label,
  value,
  onChange,
  onBlur,
  type = 'text',
  placeholder,
  disabled,
  required,
  complete,
  invalid,
  errorMessage,
  attention,
  readOnly,
  saving,
  saved,
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  complete?: boolean;
  invalid?: boolean;
  errorMessage?: string;
  attention?: boolean;
  /** SIL-1196: Email is a system field the user can't edit — labeled "Read-only", not Required/Optional. */
  readOnly?: boolean;
  saving?: boolean;
  saved?: boolean;
  autoComplete?: string;
}) {
  const markerId = `${id}-marker`;
  const errorId = `${id}-error`;
  const isInvalid = Boolean(invalid);
  return (
    <div>
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
          {label}
        </label>
        {readOnly ? (
          <span id={markerId}>
            <StatusPill tone="neutral">Read-only</StatusPill>
          </span>
        ) : (
          <FieldStatusMarker
            id={markerId}
            required={Boolean(required)}
            complete={Boolean(complete)}
            invalid={isInvalid}
            attention={Boolean(attention)}
          />
        )}
        <SavedIndicator saving={saving} saved={saved} />
      </div>
      <TextInput
        id={id}
        className="focus:ring-2 placeholder:text-muted-foreground text-foreground"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        aria-required={required}
        aria-invalid={isInvalid || undefined}
        aria-describedby={isInvalid ? `${markerId} ${errorId}` : markerId}
      />
      {isInvalid && errorMessage && (
        <p id={errorId} className="mt-1 text-xs text-status-red">
          {errorMessage}
        </p>
      )}
    </div>
  );
}

/**
 * SIL-1011: consumer-only shipping vocabulary (packages/shared's
 * CONSUMER_SHIPPING_METHOD_SLUGS) — no palletized/white glove, which are
 * business-org (DC-scale freight) options this hobbyist profile never needs.
 */
const SHIPPING_METHOD_OPTIONS = [
  { value: 'parcel', label: 'Parcel' },
  { value: 'freight', label: 'Freight' },
  { value: 'international', label: 'International' },
] as const;

type ShippingMethodSlug = (typeof SHIPPING_METHOD_OPTIONS)[number]['value'];

function PillMultiSelect<T extends string>({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  selected: T[];
  onChange: (next: T[]) => void;
}) {
  function toggle(value: T) {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  }
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-2">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map(({ value, label: optLabel }) => {
          const checked = selected.includes(value);
          return (
            <button
              key={value}
              type="button"
              onClick={() => toggle(value)}
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                // SIL-1190/SIL-1184: selection state is a neutral check + tint,
                // not the brand accent — Save stays the page's one accent focal
                // point.
                checked
                  ? 'border-border bg-muted text-foreground'
                  : 'border-border bg-card text-muted-foreground hover:border-border',
              )}
            >
              {checked && <CheckCircle2 className="size-3 shrink-0 text-green-600" aria-hidden="true" />}
              {optLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PreferencesSection({
  id,
  title,
  description,
  prefs,
  onChange,
  saving,
  saved,
}: {
  id: string;
  title: string;
  description: string;
  prefs: IndividualPreferences | null;
  onChange: (next: IndividualPreferences) => void;
  saving?: boolean;
  saved?: boolean;
}) {
  const shipping = (prefs?.prefShippingMethods ?? []) as ShippingMethodSlug[];

  return (
    <div id={id} className="pt-2 border-t border-border space-y-4">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xs font-medium text-foreground">{title}</p>
          <StatusPill tone="neutral">Optional</StatusPill>
          <SavedIndicator saving={saving} saved={saved} />
        </div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <PillMultiSelect
        label="Shipping methods"
        options={SHIPPING_METHOD_OPTIONS}
        selected={shipping}
        onChange={(next) => onChange({ ...prefs, prefShippingMethods: next })}
      />
    </div>
  );
}

/** SIL-1196: maps a checklist key to the field id its jump link scrolls to and focuses. */
function checklistJumpTargetId(key: string, addressDoneKeys: Record<string, boolean>): string {
  if (key === 'address') {
    const firstIncomplete = ADDRESS_FIELD_IDS.find((fid) => !addressDoneKeys[fid]);
    return firstIncomplete ?? ADDRESS_FIELD_IDS[0]!;
  }
  if (key === 'email') return 'field-email';
  return `field-${key}`;
}

/**
 * SIL-1196: the ONE completion counter on the page now (replaces both the
 * old live "N of 10" counter and the plain-item server checklist). Rows are
 * links that scroll to AND move real focus to their field. `doneMap` is
 * computed live from the form's own local state — not the (save-triggered)
 * server missingFields list — so it, like the per-field markers, updates on
 * every keystroke with no save required. Reused verbatim inside the sticky
 * bar's popover so both surfaces share one implementation and one set of
 * anchor links.
 */
function ChecklistPanel({
  doneMap,
  addressDoneKeys,
  onJump,
}: {
  doneMap: Record<string, boolean>;
  addressDoneKeys: Record<string, boolean>;
  onJump: (id: string) => void;
}) {
  const total = COMPLETENESS_FIELDS.length;
  const doneCount = COMPLETENESS_FIELDS.filter(({ key }) => doneMap[key]).length;
  const complete = doneCount === total;

  return (
    <div aria-live="polite" className="rounded-md border border-border bg-muted p-3">
      {complete ? (
        <p className="flex items-center gap-1.5 text-xs font-medium text-status-green">
          <CheckCircle2 className="size-3.5 shrink-0" aria-hidden="true" />
          Profile complete — buying and selling unlocked.
        </p>
      ) : (
        <>
          <p className="mb-1.5 text-xs font-medium text-foreground">
            {doneCount} of {total} complete
          </p>
          <div className="mb-2 h-1 w-full overflow-hidden rounded-full bg-border">
            <div
              className="h-full rounded-full bg-status-green transition-all"
              style={{ width: `${(doneCount / total) * 100}%` }}
            />
          </div>
          <ul className="space-y-1">
            {COMPLETENESS_FIELDS.map(({ key, label }) => {
              const done = Boolean(doneMap[key]);
              const targetId = checklistJumpTargetId(key, addressDoneKeys);
              return (
                <li key={key}>
                  <a
                    href={`#${targetId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      onJump(targetId);
                    }}
                    className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground hover:underline"
                  >
                    {done ? (
                      <CheckCircle2 className="size-3.5 shrink-0 text-status-green" aria-hidden="true" />
                    ) : (
                      <Circle className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                    )}
                    <span>{label}</span>
                    <span className="sr-only">{done ? ' — complete' : ' — incomplete'}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

type SaveOutcome =
  | { kind: 'success' }
  | { kind: 'saved-with-errors' }
  | { kind: 'incomplete'; missing: { id: string; label: string }[] }
  | { kind: 'error'; message: string };

/**
 * SIL-1196: builds the address PATCH payload the same way at every call
 * site (autosave-on-blur of any address field, the bottom Save button, and
 * seeding the autosave dedupe ref from a freshly loaded profile) — a fixed
 * key order so JSON.stringify comparisons across those call sites agree.
 */
function buildAddressPayload(
  street: string,
  city: string,
  state: string,
  postal: string,
  country: string,
): NonNullable<OrgAddress> | null {
  const address: NonNullable<OrgAddress> = {};
  if (street) address.street = street;
  if (city) address.city = city;
  if (state) address.state = state;
  if (postal) address.postal = postal;
  if (country) address.country = country;
  return Object.keys(address).length > 0 ? address : null;
}

function IndividualProfilePage() {
  const { isLoading: authLoading, canSell, canBuy } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // SIL-1210: only ever set by the Confirm-screen CTAs' `?return=` marker —
  // already validated same-origin-relative by `Route`'s `validateSearch`
  // above, so this is safe to hand straight to `navigate()` below.
  const { return: returnTo } = Route.useSearch();
  const unlocked = canSell && canBuy;
  // SIL-1812: profile completion is deferred to the Exchange. The root gate
  // sends an incomplete individual here with `?return=<exchange path>`; show
  // the banner only for that entry, not on an ordinary Settings visit.
  const exchangeEntry = !unlocked && !!returnTo;

  const [profile, setProfile] = useState<IndividualProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveOutcome, setSaveOutcome] = useState<SaveOutcome | null>(null);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [savingSections, setSavingSections] = useState<Record<string, boolean>>({});
  const [savedSections, setSavedSections] = useState<Record<string, boolean>>({});

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  // SIL-1198: dial-code selector state, independent of countryOfResidence —
  // "Country of residence stays separate and does not change" per the
  // issue's decision. Defaults via guessDefaultDialCountry in applyProfile;
  // the user can always change it.
  const [phoneCountry, setPhoneCountry] = useState<CountryCode>('US');
  // Client-side "not valid for the selected country" message, set on blur
  // (or once the field reaches a plausible length) — distinct from
  // fieldErrors.phone, which is the server's response to an actual save.
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [addrStreet, setAddrStreet] = useState('');
  const [addrCity, setAddrCity] = useState('');
  const [addrState, setAddrState] = useState('');
  const [addrPostal, setAddrPostal] = useState('');
  const [addrCountry, setAddrCountry] = useState('');
  const [countryOfResidence, setCountryOfResidence] = useState('');
  const [buyingPreferences, setBuyingPreferences] = useState<IndividualPreferences | null>(null);
  const [sellingPreferences, setSellingPreferences] = useState<IndividualPreferences | null>(null);

  // SIL-1196: sticky summary bar — visible only once the in-page summary
  // panel (ChecklistPanel + its heading) has scrolled out of view.
  const summaryRef = useRef<HTMLDivElement | null>(null);
  const [summaryVisible, setSummaryVisible] = useState(true);
  const [stickyOpen, setStickyOpen] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const node = summaryRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setSummaryVisible(entry.isIntersecting);
      },
      { threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!stickyOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setStickyOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [stickyOpen]);

  // SIL-1196: dedupes autosave — a blur with no actual change (tab-through,
  // re-blur) shouldn't fire a redundant PATCH. Seeded from the loaded
  // profile in applyProfile, then updated optimistically on every autosave
  // call below.
  const lastSavedRef = useRef<Record<string, string>>({});

  const applyProfile = useCallback((p: IndividualProfile) => {
    setProfile(p);
    setFirstName(p.firstName ?? '');
    setLastName(p.lastName ?? '');
    // SIL-1198: (1) an existing E.164-parseable phone wins — its own
    // embedded country is the most reliable signal there is; (2) else the
    // address country; (3) else a browser-locale guess; (4) else 'US'. The
    // user can always change the selector afterward.
    const dialCountry = guessDefaultDialCountry({
      existingPhone: p.phone,
      addressCountry: p.address?.country ?? null,
      localeGuess: browserLocaleCountryGuess(),
    });
    setPhoneCountry(dialCountry);
    // SIL-1198 "no backfill": an old, non-E.164 value that doesn't parse is
    // shown exactly as stored (formatPhoneForDisplay falls back to the raw
    // string) rather than being rejected or blanked.
    setPhone(p.phone ? formatPhoneForDisplay(p.phone, dialCountry) : '');
    setPhoneError(null);
    setAddrStreet(p.address?.street ?? '');
    setAddrCity(p.address?.city ?? '');
    setAddrState(p.address?.state ?? '');
    setAddrPostal(p.address?.postal ?? '');
    setAddrCountry(p.address?.country ?? '');
    setCountryOfResidence(p.countryOfResidence ?? '');
    setBuyingPreferences(p.buyingPreferences ?? null);
    setSellingPreferences(p.sellingPreferences ?? null);
    lastSavedRef.current = {
      firstName: p.firstName ?? '',
      lastName: p.lastName ?? '',
      // Seeded with what a next autosave/Save would actually send (the
      // E.164 form), not the raw stored value — so a blur with no real
      // edit right after load doesn't fire a redundant PATCH just because
      // the display format differs from the stored one.
      phone: p.phone ? (toE164(p.phone, dialCountry) ?? p.phone) : '',
      address: JSON.stringify(
        buildAddressPayload(
          p.address?.street ?? '',
          p.address?.city ?? '',
          p.address?.state ?? '',
          p.address?.postal ?? '',
          p.address?.country ?? '',
        ),
      ),
      countryOfResidence: p.countryOfResidence ?? '',
      buyingPreferences: JSON.stringify(p.buyingPreferences ?? null),
      sellingPreferences: JSON.stringify(p.sellingPreferences ?? null),
    };
  }, []);

  useEffect(() => {
    (async () => {
      const res = await api.api.client['individual-profile'].$get();
      await throwIfNotOk(res);
      const data = (await res.json()) as unknown as IndividualProfile;
      applyProfile(data);
    })()
      .catch((err: unknown) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, [applyProfile]);

  // SIL-1210: fires the `?return=` redirect exactly once, and only on an
  // incomplete→complete TRANSITION of the server's own completeness rule
  // (`unlocked`, sourced from useAuth's canSell/canBuy — the same
  // identity-query state IndividualProfileGate itself keys off of, not
  // either of this page's two client-side checklists). `wasUnlockedRef`
  // seeds from the first non-loading render without firing — a user who
  // opens an ALREADY-complete profile (e.g. re-visiting via the marker
  // after already finishing it in another tab) never gets bounced; only a
  // completion that happens ON this page counts. autosaveSection/handleSave
  // above already invalidate the identity query before this effect can see
  // `unlocked` flip, so a stale memoized identity can't suppress the
  // redirect (mirrors SIL-1207's invalidate-before-navigate fix).
  const wasUnlockedRef = useRef(false);
  const unlockedInitializedRef = useRef(false);
  const redirectFiredRef = useRef(false);
  useEffect(() => {
    if (authLoading) return;
    if (!unlockedInitializedRef.current) {
      unlockedInitializedRef.current = true;
      wasUnlockedRef.current = unlocked;
      return;
    }
    if (!redirectFiredRef.current && !wasUnlockedRef.current && unlocked && returnTo) {
      redirectFiredRef.current = true;
      toast.success('Profile complete — you can now buy and sell.');
      navigate({ to: returnTo, replace: true });
    }
    wasUnlockedRef.current = unlocked;
  }, [authLoading, unlocked, returnTo, navigate]);

  /**
   * SIL-1196: shared autosave path — PATCHes just this section's payload,
   * flips its "Saving…"/"Saved" indicator, refreshes fieldErrors for the
   * keys THIS payload touched (never clobbers another field's error), and
   * invalidates the identity query so canSell/canBuy catch up. Deliberately
   * does NOT call applyProfile — another field may be mid-edit when this
   * response lands, and overwriting its local (not-yet-saved) value with a
   * stale server snapshot would silently drop the user's in-progress typing.
   */
  const autosaveSection = useCallback(
    async (sectionKey: string, payload: Record<string, unknown>) => {
      setSavingSections((s) => ({ ...s, [sectionKey]: true }));
      try {
        const res = await api.api.client['individual-profile'].$patch({ json: payload });
        if (!res.ok) {
          await throwIfNotOk(res);
          throw new Error(`HTTP ${res.status}`);
        }
        const updated = (await res.json()) as unknown as IndividualProfile & {
          fieldErrors?: Record<string, string>;
        };
        setFieldErrors((prev) => {
          const next = { ...prev };
          for (const key of Object.keys(payload)) delete next[key];
          if (updated.fieldErrors) Object.assign(next, updated.fieldErrors);
          return next;
        });
        setSavedSections((s) => ({ ...s, [sectionKey]: true }));
        clearClientIdentity();
        queryClient.invalidateQueries({ queryKey: qk.identity() });
      } catch {
        setSavedSections((s) => ({ ...s, [sectionKey]: false }));
      } finally {
        setSavingSections((s) => ({ ...s, [sectionKey]: false }));
      }
    },
    [queryClient],
  );

  function handleSimpleFieldBlur(key: 'firstName' | 'lastName', value: string) {
    setTouched((t) => ({ ...t, [key]: true }));
    const normalized = value.trim() ? value : '';
    if (lastSavedRef.current[key] === normalized) return;
    lastSavedRef.current[key] = normalized;
    void autosaveSection(key, { [key]: normalized || null });
  }

  function handlePhoneChange(formatted: string) {
    setPhone(formatted);
    setSavedSections((s) => ({ ...s, phone: false }));
    // A fresh edit invalidates any stale error from a previous blur/save —
    // no flash, but also no stale red state lingering on a corrected value.
    setPhoneError(null);
    setFieldErrors((prev) => {
      if (!prev['phone']) return prev;
      const next = { ...prev };
      delete next['phone'];
      return next;
    });
  }

  function handlePhoneCountryChange(next: CountryCode) {
    setPhoneCountry(next);
    setPhoneError(null);
    setSavedSections((s) => ({ ...s, phone: false }));
    setFieldErrors((prev) => {
      if (!prev['phone']) return prev;
      const rest = { ...prev };
      delete rest['phone'];
      return rest;
    });
    // Reformat the already-typed digits for the newly selected country —
    // the number itself doesn't change, only how it's grouped/displayed.
    setPhone((prev) => (prev.trim() ? reformatPhoneWithCaret(prev, prev.length, next).formatted : prev));
  }

  /**
   * SIL-1198: validation runs here — on blur, per the issue's acceptance
   * criteria (or once handlePhoneChange's live `phoneIsValid` below reaches
   * a plausible length; that only affects the status marker, not this
   * function). A paste is normalized here too, since it also lands via
   * handlePhoneChange first, then blur: `isValidPhoneForCountry` checks
   * *valid*, not just *possible*, and a failure gets a country-named
   * message (`phoneValidationMessage`) instead of a generic one.
   */
  function handlePhoneBlur() {
    setTouched((t) => ({ ...t, phone: true }));
    const trimmed = phone.trim();
    if (!trimmed) {
      setPhoneError(null);
      if (lastSavedRef.current['phone'] === '') return;
      lastSavedRef.current['phone'] = '';
      void autosaveSection('phone', { phone: null });
      return;
    }
    if (!isValidPhoneForCountry(trimmed, phoneCountry)) {
      setPhoneError(phoneValidationMessage(trimmed, phoneCountry));
      return;
    }
    const e164 = toE164(trimmed, phoneCountry);
    if (!e164) {
      setPhoneError(phoneValidationMessage(trimmed, phoneCountry));
      return;
    }
    setPhoneError(null);
    // Re-display in the canonical national format for this country — e.g.
    // a pasted "555-000-0000" becomes "(555) 000-0000".
    setPhone(formatPhoneForDisplay(e164, phoneCountry));
    if (lastSavedRef.current['phone'] === e164) return;
    lastSavedRef.current['phone'] = e164;
    void autosaveSection('phone', { phone: e164 });
  }

  function handleAddressFieldBlur(key: string) {
    setTouched((t) => ({ ...t, [key]: true }));
    const address = buildAddressPayload(addrStreet, addrCity, addrState, addrPostal, addrCountry);
    const serialized = JSON.stringify(address);
    if (lastSavedRef.current['address'] === serialized) return;
    lastSavedRef.current['address'] = serialized;
    void autosaveSection('address', { address });
  }

  function handleCountryBlur() {
    setTouched((t) => ({ ...t, countryOfResidence: true }));
    const normalized = countryOfResidence.trim().toUpperCase();
    if (!normalized) return;
    if (lastSavedRef.current['countryOfResidence'] === normalized) return;
    lastSavedRef.current['countryOfResidence'] = normalized;
    void autosaveSection('countryOfResidence', { countryOfResidence: normalized });
  }

  function handleBuyingPreferencesChange(next: IndividualPreferences) {
    setBuyingPreferences(next);
    setSavedSections((s) => ({ ...s, buyingPreferences: false }));
    const serialized = JSON.stringify(next);
    if (lastSavedRef.current['buyingPreferences'] === serialized) return;
    lastSavedRef.current['buyingPreferences'] = serialized;
    void autosaveSection('buyingPreferences', { buyingPreferences: next });
  }

  function handleSellingPreferencesChange(next: IndividualPreferences) {
    setSellingPreferences(next);
    setSavedSections((s) => ({ ...s, sellingPreferences: false }));
    const serialized = JSON.stringify(next);
    if (lastSavedRef.current['sellingPreferences'] === serialized) return;
    lastSavedRef.current['sellingPreferences'] = serialized;
    void autosaveSection('sellingPreferences', { sellingPreferences: next });
  }

  // SIL-1198: valid for the selected country, not just non-empty — feeds
  // the field's status marker (green check only once actually valid, per
  // the issue's acceptance criteria), the "still needed" list below, and
  // the 6-item checklist.
  const phoneIsValid = useMemo(() => isValidPhoneForCountry(phone, phoneCountry), [phone, phoneCountry]);
  const phoneNonEmptyInvalid = phone.trim().length > 0 && !phoneIsValid;

  // SIL-1196: current typed values, keyed to match REQUIRED_INPUT_FIELDS —
  // drives both the live "N fields still needed" state (the Save button's
  // "Save and unlock" framing) and the post-save incomplete summary.
  const currentFieldValues = useMemo<Record<string, string>>(
    () => ({
      firstName,
      lastName,
      // SIL-1198: an entered-but-invalid phone counts as "still needed" —
      // it can't be saved as typed, so it shouldn't read as done.
      phone: phoneIsValid ? phone : '',
      addrStreet,
      addrCity,
      addrState,
      addrPostal,
      addrCountry,
      countryOfResidence,
    }),
    [firstName, lastName, phone, phoneIsValid, addrStreet, addrCity, addrState, addrPostal, addrCountry, countryOfResidence],
  );

  const liveMissingRequired = useMemo(
    () => REQUIRED_INPUT_FIELDS.filter((f) => !currentFieldValues[f.key]?.trim()),
    [currentFieldValues],
  );
  const localComplete = liveMissingRequired.length === 0;

  // SIL-1196: the 6-item checklist's doneMap — the ONE completion signal on
  // the page, computed live from local state (not the server's post-save
  // missingFields list).
  const checklistDoneMap = useMemo<Record<string, boolean>>(
    () => ({
      firstName: Boolean(firstName.trim()),
      lastName: Boolean(lastName.trim()),
      email: Boolean(profile?.email),
      phone: phoneIsValid,
      address: Boolean(
        addrStreet.trim() && addrCity.trim() && addrState.trim() && addrPostal.trim() && addrCountry.trim(),
      ),
      countryOfResidence: Boolean(countryOfResidence.trim()),
    }),
    [
      firstName,
      lastName,
      profile?.email,
      phoneIsValid,
      addrStreet,
      addrCity,
      addrState,
      addrPostal,
      addrCountry,
      countryOfResidence,
    ],
  );

  const addressDoneKeys = useMemo<Record<string, boolean>>(
    () => ({
      'field-addrStreet': Boolean(addrStreet.trim()),
      'field-addrCity': Boolean(addrCity.trim()),
      'field-addrState': Boolean(addrState.trim()),
      'field-addrPostal': Boolean(addrPostal.trim()),
      'field-addrCountry': Boolean(addrCountry.trim()),
    }),
    [addrStreet, addrCity, addrState, addrPostal, addrCountry],
  );
  const addressDoneCount = Object.values(addressDoneKeys).filter(Boolean).length;

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveAttempted(true);
    setSaveOutcome(null);
    try {
      const address = buildAddressPayload(addrStreet, addrCity, addrState, addrPostal, addrCountry);
      const normalizedCountry = countryOfResidence.trim().toUpperCase();

      // SIL-1198: send E.164 when the number validates for the selected
      // country; otherwise fall back to the raw typed value so the API's
      // own `normalizePhoneToE164` still gets a chance (and, on failure,
      // still reports a specific `fieldErrors.phone` — belt and suspenders,
      // same reasoning as countryOfResidence's omit-when-blank guard above).
      const phoneTrimmed = phone.trim();
      const phonePayload = phoneTrimmed ? (toE164(phoneTrimmed, phoneCountry) ?? phoneTrimmed) : null;

      const payload = {
        firstName: firstName || null,
        lastName: lastName || null,
        phone: phonePayload,
        address,
        // Declared directly by the user — never derived from address.country
        // above (SIL-1011 / migration 758's compliance rule). Omitted (not
        // sent as '') when blank so a not-yet-filled field never reaches the
        // API's ISO-2 check. The select below only ever produces a valid ISO
        // code once non-blank, but the omit-when-blank guard stays as belt
        // and suspenders.
        ...(normalizedCountry ? { countryOfResidence: normalizedCountry } : {}),
        buyingPreferences,
        sellingPreferences,
      };

      const res = await api.api.client['individual-profile'].$patch({ json: payload });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      const updated = (await res.json()) as unknown as IndividualProfile & {
        fieldErrors?: Record<string, string>;
      };
      applyProfile(updated);
      // SIL-1190: a per-field error (e.g. a stale/invalid countryOfResidence
      // from an older cached bundle) does not mean the save failed — every
      // other field in this same PATCH still persisted. Surface it
      // alongside the outcome rather than treating it as a hard failure.
      setFieldErrors(updated.fieldErrors ?? {});
      setSavedSections({
        firstName: true,
        lastName: true,
        phone: !(updated.fieldErrors && updated.fieldErrors['phone']),
        address: true,
        countryOfResidence: !(updated.fieldErrors && updated.fieldErrors['countryOfResidence']),
        buyingPreferences: true,
        sellingPreferences: true,
      });

      // canSell/canBuy are derived server-side from this same profile — drop
      // the memoized identity so the next read (this page's checklist, the
      // dashboard, any gated action) reflects the new completeness state
      // immediately instead of the pre-save value for the rest of the session.
      clearClientIdentity();
      queryClient.invalidateQueries({ queryKey: qk.identity() });

      // SIL-1196: "honest Save" — a partial save still persists (decision:
      // partial saves persist), but the button's own feedback says plainly
      // what's still missing instead of a bare "Saved." that reads as done.
      const missing = REQUIRED_INPUT_FIELDS.filter((f) => !currentFieldValues[f.key]?.trim());
      if (missing.length > 0) {
        setSaveOutcome({ kind: 'incomplete', missing });
        focusAndScroll(missing[0]!.id);
      } else if (updated.fieldErrors && Object.keys(updated.fieldErrors).length > 0) {
        setSaveOutcome({ kind: 'saved-with-errors' });
      } else {
        setSaveOutcome({ kind: 'success' });
      }
    } catch (ex: unknown) {
      setSaveOutcome({ kind: 'error', message: (ex as Error).message || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  }

  if (authLoading || loading) {
    return <LoadingShell rows={6} />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  const saveButtonLabel = saving ? 'Saving…' : localComplete && !unlocked ? 'Save and unlock' : 'Save';

  function jumpToChecklistField(id: string) {
    setStickyOpen(false);
    focusAndScroll(id);
  }

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <PageHeader title="Complete your profile" pinTo="/individual-profile" />

      {/* SIL-1812: Exchange entry banner (see `exchangeEntry`). */}
      {exchangeEntry && (
        <div role="status" className="rounded-md border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-800">
          Complete your profile to buy and sell.
        </div>
      )}

      {/*
        SIL-1196: one-line sticky bar, shown only once the in-page summary
        panel below has scrolled out of view. Clicking it opens the same
        checklist (same anchor links) in a popover, so the user never has to
        scroll back to the top to see where they stand.
      */}
      {!summaryVisible && (
        <div className="fixed inset-x-0 top-0 z-40 border-b border-border bg-card shadow-sm">
          <div className="mx-auto max-w-lg px-4 py-2">
            <button
              type="button"
              onClick={() => setStickyOpen((v) => !v)}
              aria-expanded={stickyOpen}
              className="flex w-full items-center justify-between gap-3 text-left"
            >
              <span className="text-xs font-medium text-foreground">
                Profile · {COMPLETENESS_FIELDS.filter(({ key }) => checklistDoneMap[key]).length} of{' '}
                {COMPLETENESS_FIELDS.length} complete
              </span>
              <span className="h-1 w-24 shrink-0 overflow-hidden rounded-full bg-border">
                <span
                  className="block h-full rounded-full bg-status-green transition-all"
                  style={{
                    width: `${(COMPLETENESS_FIELDS.filter(({ key }) => checklistDoneMap[key]).length / COMPLETENESS_FIELDS.length) * 100}%`,
                  }}
                />
              </span>
            </button>
            {stickyOpen && (
              <div className="mt-2">
                <ChecklistPanel doneMap={checklistDoneMap} addressDoneKeys={addressDoneKeys} onJump={jumpToChecklistField} />
              </div>
            )}
          </div>
        </div>
      )}

      <Card className="space-y-5">
        <div ref={summaryRef} className="space-y-5">
          <PanelTitle
            title="Your profile"
            description="This is your personal identity record — completing it unlocks both buying and selling in one step."
          />

          <ChecklistPanel doneMap={checklistDoneMap} addressDoneKeys={addressDoneKeys} onJump={(id) => focusAndScroll(id)} />
        </div>

        <form onSubmit={handleSave} className="space-y-4">
          <TextField
            id="field-firstName"
            label="First name"
            value={firstName}
            onChange={(v) => {
              setFirstName(v);
              setSavedSections((s) => ({ ...s, firstName: false }));
            }}
            onBlur={() => handleSimpleFieldBlur('firstName', firstName)}
            placeholder="Ada"
            required
            complete={firstName.trim().length > 0}
            attention={touched.firstName || saveAttempted}
            saving={savingSections.firstName}
            saved={savedSections.firstName}
            autoComplete="given-name"
          />
          <TextField
            id="field-lastName"
            label="Last name"
            value={lastName}
            onChange={(v) => {
              setLastName(v);
              setSavedSections((s) => ({ ...s, lastName: false }));
            }}
            onBlur={() => handleSimpleFieldBlur('lastName', lastName)}
            placeholder="Lovelace"
            required
            complete={lastName.trim().length > 0}
            attention={touched.lastName || saveAttempted}
            saving={savingSections.lastName}
            saved={savedSections.lastName}
            autoComplete="family-name"
          />
          <TextField
            id="field-email"
            label="Email"
            value={profile?.email ?? ''}
            onChange={() => {}}
            type="email"
            disabled
            readOnly
            complete={Boolean(profile?.email)}
            autoComplete="email"
          />
          <PhoneField
            value={phone}
            country={phoneCountry}
            onCountryChange={handlePhoneCountryChange}
            onChange={handlePhoneChange}
            onBlur={handlePhoneBlur}
            required
            complete={phoneIsValid}
            invalid={phoneNonEmptyInvalid || Boolean(fieldErrors['phone'])}
            errorMessage={phoneError ?? fieldErrors['phone']}
            attention={touched.phone || saveAttempted}
            saving={savingSections.phone}
            saved={savedSections.phone}
          />

          <div className="pt-2 border-t border-border space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs font-medium text-muted-foreground">Address</p>
              <StatusPill tone={addressDoneCount === 5 ? 'green' : 'neutral'}>
                {addressDoneCount} of 5 required complete
              </StatusPill>
              <SavedIndicator saving={savingSections.address} saved={savedSections.address} />
            </div>
            <TextField
              id="field-addrStreet"
              label="Street"
              value={addrStreet}
              onChange={(v) => {
                setAddrStreet(v);
                setSavedSections((s) => ({ ...s, address: false }));
              }}
              onBlur={() => handleAddressFieldBlur('field-addrStreet')}
              placeholder="e.g., 123 Main St"
              required
              complete={addrStreet.trim().length > 0}
              attention={touched['field-addrStreet'] || saveAttempted}
              autoComplete="address-line1"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField
                id="field-addrCity"
                label="City"
                value={addrCity}
                onChange={(v) => {
                  setAddrCity(v);
                  setSavedSections((s) => ({ ...s, address: false }));
                }}
                onBlur={() => handleAddressFieldBlur('field-addrCity')}
                placeholder="e.g., San Francisco"
                required
                complete={addrCity.trim().length > 0}
                attention={touched['field-addrCity'] || saveAttempted}
                autoComplete="address-level2"
              />
              <TextField
                id="field-addrState"
                label="State"
                value={addrState}
                onChange={(v) => {
                  setAddrState(v);
                  setSavedSections((s) => ({ ...s, address: false }));
                }}
                onBlur={() => handleAddressFieldBlur('field-addrState')}
                placeholder="CA"
                required
                complete={addrState.trim().length > 0}
                attention={touched['field-addrState'] || saveAttempted}
                autoComplete="address-level1"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField
                id="field-addrPostal"
                label="Postal code"
                value={addrPostal}
                onChange={(v) => {
                  setAddrPostal(v);
                  setSavedSections((s) => ({ ...s, address: false }));
                }}
                onBlur={() => handleAddressFieldBlur('field-addrPostal')}
                placeholder="e.g., 94103"
                required
                complete={addrPostal.trim().length > 0}
                attention={touched['field-addrPostal'] || saveAttempted}
                autoComplete="postal-code"
              />
              <TextField
                id="field-addrCountry"
                label="Country"
                value={addrCountry}
                onChange={(v) => {
                  setAddrCountry(v);
                  setSavedSections((s) => ({ ...s, address: false }));
                }}
                onBlur={() => handleAddressFieldBlur('field-addrCountry')}
                placeholder="US"
                required
                complete={addrCountry.trim().length > 0}
                attention={touched['field-addrCountry'] || saveAttempted}
                autoComplete="country-name"
              />
            </div>
          </div>

          <div className="pt-2 border-t border-border space-y-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs font-medium text-foreground">Country of residence</p>
                <StatusPill tone="neutral">Required</StatusPill>
                <SavedIndicator saving={savingSections.countryOfResidence} saved={savedSections.countryOfResidence} />
              </div>
              <p className="text-xs text-muted-foreground">
                Required to unlock buying and selling — this is declared separately from your shipping
                address above, not derived from it.
              </p>
            </div>
            <div>
              <label
                htmlFor="field-countryOfResidence"
                className="mb-1 flex flex-wrap items-center gap-2 text-xs font-medium text-muted-foreground"
              >
                <span>Country of residence</span>
                <FieldStatusMarker
                  id="field-countryOfResidence-marker"
                  required
                  complete={countryOfResidence.trim().length > 0}
                  invalid={Boolean(fieldErrors['countryOfResidence'])}
                  attention={touched.countryOfResidence || saveAttempted}
                />
              </label>
              <select
                id="field-countryOfResidence"
                value={countryOfResidence}
                onChange={(e) => {
                  setCountryOfResidence(e.target.value);
                  setSavedSections((s) => ({ ...s, countryOfResidence: false }));
                }}
                onBlur={handleCountryBlur}
                autoComplete="country"
                aria-required="true"
                aria-invalid={Boolean(fieldErrors['countryOfResidence']) || undefined}
                aria-describedby={
                  fieldErrors['countryOfResidence']
                    ? 'field-countryOfResidence-marker field-countryOfResidence-error'
                    : 'field-countryOfResidence-marker'
                }
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <option value="">Select a country…</option>
                {COUNTRY_OPTIONS.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
              {fieldErrors['countryOfResidence'] && (
                <p id="field-countryOfResidence-error" className="mt-1 text-xs text-status-red">
                  {fieldErrors['countryOfResidence']}
                </p>
              )}
            </div>
          </div>

          <>
              <PreferencesSection
                id="section-buyingPreferences"
                title="Buying preferences"
                description="Helps us surface relevant listings. Not required to unlock buying."
                prefs={buyingPreferences}
                onChange={handleBuyingPreferencesChange}
                saving={savingSections.buyingPreferences}
                saved={savedSections.buyingPreferences}
              />
              <PreferencesSection
                id="section-sellingPreferences"
                title="Selling preferences"
                description="Helps us match your inventory to buyers. Not required to unlock selling."
                prefs={sellingPreferences}
                onChange={handleSellingPreferencesChange}
                saving={savingSections.sellingPreferences}
                saved={savedSections.sellingPreferences}
              />
          </>

          <div className="flex items-center gap-3 pt-2 border-t border-border">
            <Button type="submit" disabled={saving}>
              {saveButtonLabel}
            </Button>
            {saveOutcome?.kind === 'success' && <span className="text-sm text-muted-foreground">Saved.</span>}
            {saveOutcome?.kind === 'saved-with-errors' && (
              <span className="text-sm text-muted-foreground">Saved, with one field skipped — see below.</span>
            )}
            {saveOutcome?.kind === 'error' && <span className="text-sm text-status-red">{saveOutcome.message}</span>}
          </div>

          {saveOutcome?.kind === 'incomplete' && (
            <div role="alert" className="rounded-md border border-status-amber/30 bg-status-amber/10 p-3 text-xs">
              <p className="font-medium text-status-amber">
                {saveOutcome.missing.length} field{saveOutcome.missing.length === 1 ? '' : 's'} still needed
              </p>
              <ul className="mt-1 space-y-1">
                {saveOutcome.missing.map((f) => (
                  <li key={f.id}>
                    <a
                      href={`#${f.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        focusAndScroll(f.id);
                      }}
                      className="text-status-amber underline underline-offset-2"
                    >
                      {f.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
}
